import React, { Component } from 'react';

import { Eye, Megaphone, Menu, Send } from 'lucide-react';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { LocationContext } from 'common/containers/RouterContainer';
import connect from 'common/core/connect';
import publicConfig from 'common/core/publicConfig';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import NotificationsMenu from 'common/notifications/NotificationsMenu';
import SpinnerV2 from 'common/SpinnerV2';
import { cn } from 'common/style/utils';
import AdminAutopilotImportPopover from 'common/subdomain/admin/AdminAutopilotImportPopover';
import OnboardingProgressIndicator from 'common/subdomain/admin/AdminOnboarding/InAppOnboarding/OnboardingProgressIndicator';
import Tappable from 'common/Tappable';
import CountBadge from 'common/ui/CountBadge';
import Tooltip from 'common/uiV3/Tooltip';
import parseAPIResponse from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import AdminNavAccountMenu from './v2/AdminV2Nav/AdminNavAccountMenu';

import WhiteCannyIcon from 'img/canny-icon-white.svg';

import 'css/components/subdomain/admin/_AdminNav.scss';

const navIconClassName = cn(
  'text-white dark:text-foreground',
  'bg-indigo-100 dark:bg-sidebar-accent',
  'hover:bg-indigo-110 dark:hover:bg-indigo-50',
);

const navLinkClassName = cn(
  'navLink',
  'text-white dark:text-foreground',
  'hover:bg-indigo-60 dark:hover:bg-indigo-30',
  '[&.active]:bg-indigo-100 dark:[&.active]:bg-sidebar-accent',
  'hover:bg-indigo-100 dark:hover:bg-sidebar-accent',
);

class AdminNav extends Component {
  static propTypes = {
    company: PropTypes.shape({
      showChangelog: PropTypes.bool,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    onboarding: PropTypes.object,
  };

  state = {
    changelogMenuOpen: false,
    mobileMenuOpen: false,
    count: 0,
    countLoading: true,
  };

  constructor(props, context) {
    super(props, context);

    this.changelogButtonRef = React.createRef();
    this.containerRef = React.createRef();
  }

  async fetchNavInboxCount() {
    const { company } = this.props;

    if (company.queueAutomation?.enabled) {
      return;
    }

    const response = await AJAX.post('/api/queue/count');
    const { parsedResponse, error } = parseAPIResponse(response, {
      isSuccessful: (parsedResponse) => !!parsedResponse.result,
    });

    if (error) {
      this.setState({ countLoading: false });
      return;
    }

    this.setState({ count: parsedResponse.result.count, countLoading: false });
  }

  componentDidMount() {
    document.addEventListener('click', this.onDocumentClick, false);
    Canny('initChangelog', {
      appID: publicConfig('cannyCompanyID'),
      position: 'bottom',
      align: 'right',
    });
    this.fetchNavInboxCount();
  }

  componentDidUpdate(prevProps) {
    const { mobileMenuOpen } = this.state;
    const { location, company } = this.props;
    const shouldCloseMobileMenu = location !== prevProps.location && mobileMenuOpen;

    if (shouldCloseMobileMenu) {
      this.setState({ mobileMenuOpen: false });
    }

    if (company.queueAutomation?.enabled !== prevProps.company.queueAutomation?.enabled) {
      this.fetchNavInboxCount();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentClick, false);
  }

  onDocumentClick = (e) => {
    this.toggleChangelogMenuOpen(e);

    const { mobileMenuOpen } = this.state;
    if (!mobileMenuOpen) {
      return;
    }

    const container = this.containerRef.current;
    if (!container?.contains(e.target)) {
      // touchend happens outside of menu, close menu
      this.setState({ mobileMenuOpen: false });
      return;
    }
  };

  toggleChangelogMenuOpen = (e) => {
    const { changelogMenuOpen } = this.state;
    const changelogButton = this.changelogButtonRef.current;
    if (!changelogMenuOpen && changelogButton?.contains(e.target)) {
      this.setState({ changelogMenuOpen: true });
      return;
    }
    this.setState({ changelogMenuOpen: false });
  };

  renderChangelogIcon() {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <button
            className={cn('changelogIcon unstyled-button', navIconClassName)}
            data-canny-changelog
            ref={this.changelogButtonRef}>
            <div className="navIconContainer">
              <Megaphone size="20" />
            </div>
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content>New in Canny</Tooltip.Content>
      </Tooltip>
    );
  }

  renderChangelogLink() {
    const { company } = this.props;
    if (!company.showChangelog) {
      return null;
    }

    const {
      location: { pathname },
    } = this.props;
    const isChangelogActive = pathname.startsWith('/admin/changelog');
    const changelogLinkClassName = cn(navLinkClassName, {
      active: isChangelogActive,
    });

    return (
      <Link className={changelogLinkClassName} to="/admin/changelog">
        Changelog
      </Link>
    );
  }

  renderPreviewIcon() {
    const {
      board,
      entry,
      location: { pathname },
      post,
    } = this.props;

    const hideURLs = [
      '/admin/users',
      '/admin/settings',
      '/admin/changelog/create',
      '/admin/moderation',
    ];
    const shouldHide = hideURLs.some((url) => pathname.startsWith(url));
    if (shouldHide) {
      return null;
    }

    let redirectURL = '/';
    const isChangelog = pathname.startsWith('/admin/changelog');
    const isChangelogList = pathname === '/admin/changelog';

    if (isChangelogList) {
      redirectURL = `/changelog`;
    } else if (isChangelog && entry) {
      const entryUnpublished = entry.status === 'draft' || entry.status === 'scheduled';
      if (entryUnpublished) {
        return null;
      }

      if (entry.urlName) {
        redirectURL = `/changelog/${entry.urlName}`;
      }
    }

    const hasBoard = board && board.urlName;
    if (pathname.startsWith('/admin/feedback/') && hasBoard) {
      redirectURL =
        post && !post.error ? `/${board.urlName}/p/${post.urlName}` : `/${board.urlName}`;
    }

    return (
      <Tooltip>
        <Tooltip.Trigger>
          <Link to={redirectURL} className={cn('previewIcon', navIconClassName)}>
            <div className="navIconContainer">
              <Eye strokeWidth="1.7" size="24" />
            </div>
          </Link>
        </Tooltip.Trigger>
        <Tooltip.Content>Public view</Tooltip.Content>
      </Tooltip>
    );
  }

  renderAutopilotImportIcon() {
    return (
      <AdminAutopilotImportPopover
        side="bottom"
        align="end"
        sideOffset={12}
        trigger={
          <span>
            <Tooltip>
              <Tooltip.Trigger>
                <div className={cn('autopilotImportIcon', navIconClassName)}>
                  <div className="navIconContainer">
                    <Send size="20" />
                  </div>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content>Send to Autopilot</Tooltip.Content>
            </Tooltip>
          </span>
        }
      />
    );
  }

  renderMobileMenu() {
    const { mobileMenuOpen } = this.state;
    if (!mobileMenuOpen) {
      return null;
    }

    return (
      <div className="mobileMenu">
        <Link className="menuLink" to="/admin">
          Home
        </Link>
        <Link className="menuLink" to="/admin/feedback">
          Feedback
        </Link>
        <Link className="menuLink" to="/admin/roadmap">
          Roadmap
        </Link>
        <Link className="menuLink" to="/admin/users">
          Users
        </Link>
        <Link className="menuLink" to="/admin/changelog">
          Changelog
        </Link>
        <Link className="menuLink" to="/admin/autopilot">
          Autopilot
        </Link>
      </div>
    );
  }

  toggleMobileMenuOpen = () => {
    this.setState((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }));
  };

  render() {
    const {
      location: { pathname },
      company,
    } = this.props;
    const { count, countLoading } = this.state;
    const isFeedbackActive =
      pathname.startsWith('/admin/feedback') || pathname.startsWith('/admin/moderation');
    const feedbackLinkClassName = cn(navLinkClassName, {
      active: isFeedbackActive,
    });

    const isUsersActive = pathname.startsWith('/admin/users');
    const usersLinkClassName = cn(navLinkClassName, {
      active: isUsersActive,
    });

    const isRoadmapActive = pathname.startsWith('/admin/roadmap');
    const roadmapLinkClassName = cn(navLinkClassName, {
      active: isRoadmapActive,
    });

    return (
      <div className={cn('adminNav bg-indigo-90 dark:bg-sidebar')}>
        <div className="mainNav">
          <div className="leftContainer">
            <div className="relative">
              <Link className="logo relative" to="/admin">
                <OnboardingProgressIndicator className="right-2.5 top-2.5" />
                <LazyLoadedImage alt="Canny Home" src={WhiteCannyIcon} />
              </Link>
            </div>
            <div className="desktopLinks">
              <Link
                className={feedbackLinkClassName}
                disabled={isFeedbackActive}
                to="/admin/feedback">
                Feedback
              </Link>
              <Link className={roadmapLinkClassName} to="/admin/roadmap">
                Roadmap
              </Link>
              <Link className={usersLinkClassName} to="/admin/users">
                Users
              </Link>
              {this.renderChangelogLink()}
              <Link
                className={cn(navLinkClassName, 'withBadge', {
                  active: pathname.startsWith('/admin/autopilot'),
                })}
                to="/admin/autopilot">
                Autopilot{' '}
                {!company.queueAutomation?.enabled && count ? (
                  <CountBadge size="small" count={count} max={99} variant="alert" />
                ) : null}
                {!company.queueAutomation?.enabled && countLoading && <SpinnerV2 size="small" />}
              </Link>
              <Link className={navLinkClassName} to="/admin/reporting">
                Reporting
              </Link>
            </div>
            <div className="mobileMenuContainer" ref={this.containerRef}>
              <Tappable onTap={this.toggleMobileMenuOpen}>
                <Menu size="32" />
              </Tappable>
              {this.renderMobileMenu()}
            </div>
          </div>
          <div className="rightContainer">
            {this.renderPreviewIcon()}
            {this.renderAutopilotImportIcon()}
            {this.renderChangelogIcon()}
            <NotificationsMenu iconStyle="light" linkToAdmin={true} />
            <AdminNavAccountMenu menuPosition="bottom" />
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  connect((state) => ({
    onboarding: state.onboarding.data,
  })),
  withContexts(
    {
      company: CompanyContext,
      location: LocationContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminNav);
